import { Button, Group, ScrollArea, Stack, Tabs, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useState } from 'react';

import { Customer, EnvironmentCountries } from '@e-origin/shared';

import { DatePickerInput, LeavePageNotification, Select, StickyContainer, TextInput } from '@components';
import { EditCustomerType } from '@enums';
import { useSendTransitGuaranteeAccessCode } from '@hooks/customer';
import { useSendTransitGuaranteeRequest } from '@hooks/customer/useSendTransitGuaranteeRequest';
import { useDeclarant } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';
import { TransitGuaranteeCustomsQueryResponseDisplay } from './TransitGuaranteeCustomsQueryResponseDisplay';
import { TransitGuaranteeErrors } from './TransitGuaranteeErrors';
import { TransitGuaranteeView } from './TransitGuaranteeView';
import { useCustomerViewTransitGuaranteesFormInitializer } from './useCustomerViewTransitGuaranteesFormInitializer';

interface CustomerViewTransitGuaranteesProps {
  handleSubmitCustomer(type: EditCustomerType, childForm: UseFormReturnType<any>): void;
  customerDetails: Customer;
  isCreate: boolean;
  isButtonDisabled: boolean;
}

// eslint-disable-next-line max-statements
export const CustomerViewTransitGuarantees: React.FC<CustomerViewTransitGuaranteesProps> = ({
  customerDetails,
  isButtonDisabled,
  handleSubmitCustomer,
  isCreate,
}) => {
  const { form, buildFormValues } = useCustomerViewTransitGuaranteesFormInitializer({ customerDetails });
  const { data: declarant } = useDeclarant();
  const { data: settings } = useSettings();
  const sendAccessCode = useSendTransitGuaranteeAccessCode();
  const sendGuaranteesRequest = useSendTransitGuaranteeRequest();
  const [selectedGuaranteeIdx, setSelectedGuaranteeIdx] = useState<number | null>(null);

  useEffect(() => {
    if (customerDetails) {
      form.setValues(buildFormValues(customerDetails));
      form.resetDirty({} as any);
    }
  }, [customerDetails]);

  useEffect(() => {
    if (form.values.transitGuarantees.length > 0 && selectedGuaranteeIdx === null) {
      setSelectedGuaranteeIdx(0);
    }
  }, [form.values.transitGuarantees.length]);

  const addGuarantee = () => {
    const newGuarantee = {
      type: '',
      queryIdentifier: null,
      requesterRole: null,
      ownerId: '',
      accessCodeNew: '',
      holderId: '',
      masterAccessCode: '',
      reference: {
        grn: '',
        accessCode: '',
        amountToBeCovered: '',
        currencyCode: '',
      },
    };
    const updated = [...form.values.transitGuarantees, newGuarantee];
    form.setFieldValue('transitGuarantees', updated);
    setSelectedGuaranteeIdx(updated.length - 1);
  };

  const removeGuarantee = (guaranteeIdx: number) => {
    const guarantees = [...form.values.transitGuarantees];
    guarantees.splice(guaranteeIdx, 1);
    form.setFieldValue('transitGuarantees', guarantees);
    if (selectedGuaranteeIdx !== null) {
      if (guaranteeIdx === selectedGuaranteeIdx) {
        setSelectedGuaranteeIdx(guarantees.length > 0 ? 0 : null);
      } else if (guaranteeIdx < selectedGuaranteeIdx) {
        setSelectedGuaranteeIdx(selectedGuaranteeIdx - 1);
      }
    }
  };

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />
      <ScrollArea h="100%" offsetScrollbars="y">
        <Group mb="xl" px="md" justify="space-between" align="flex-end">
          <Title order={5} fw={600}>
            Manage Guarantees
          </Title>
          {settings?.country === EnvironmentCountries.NL && (
            <Group>
              <Button onClick={() => sendGuaranteesRequest.mutate({ params: { id: customerDetails._id } })} loading={sendGuaranteesRequest.isPending}>
                Query
              </Button>
              <TextInput label="Requester ID" value={declarant?.customsAuthentication?.enterpriseNumber || ''} disabled w={160} />
              <Select
                {...form.getInputProps('requestForTransitGuarantee.requesterRole')}
                label="Requester role"
                data={[
                  { value: '1', label: '1 - Holder of the transit procedure' },
                  { value: '2', label: '2 - Guarantor' },
                ]}
                value={form.values.requestForTransitGuarantee.requesterRole?.toString() ?? null}
                onChange={(value) => form.setFieldValue('requestForTransitGuarantee.requesterRole', value ? Number(value) : null)}
              />
              <DatePickerInput label="Period from" w={150} {...form.getInputProps('requestForTransitGuarantee.periodFromDate')} clearable />
              <DatePickerInput label="Period to" w={150} {...form.getInputProps('requestForTransitGuarantee.periodToDate')} clearable />
            </Group>
          )}
        </Group>
        <Group align="flex-start">
          <Tabs
            value={selectedGuaranteeIdx?.toString() ?? null}
            onChange={(value) => setSelectedGuaranteeIdx(value ? Number(value) : null)}
            orientation="vertical"
          >
            <Tabs.List>
              <Stack>
                <Title order={6} fw={500} ml="md">
                  GRN
                </Title>
                {form.values.transitGuarantees.map((guarantee, guaranteeIdx) => (
                  <Tabs.Tab key={guaranteeIdx} value={guaranteeIdx.toString()}>
                    <Text size="sm">{guarantee.reference.grn || 'N/A'}</Text>
                  </Tabs.Tab>
                ))}
                <Button variant="light" size="sm" onClick={addGuarantee} mt="xs" mx="xs">
                  + Add Guarantee
                </Button>
              </Stack>
            </Tabs.List>
          </Tabs>
          {selectedGuaranteeIdx !== null && form.values.transitGuarantees[selectedGuaranteeIdx] && (
            <Stack>
              <TransitGuaranteeView
                guarantee={form.values.transitGuarantees[selectedGuaranteeIdx]}
                onUpdateAccessCode={() => {
                  handleSubmitCustomer(EditCustomerType.GUARANTEES_TRANSIT, form);
                  sendAccessCode.mutate({ params: { id: customerDetails._id, grn: form.values.transitGuarantees[selectedGuaranteeIdx].reference.grn } });
                }}
                onDelete={() => removeGuarantee(selectedGuaranteeIdx)}
                onSave={(values) => form.setFieldValue(`transitGuarantees.${selectedGuaranteeIdx}`, values)}
              />
            </Stack>
          )}
        </Group>
        <TransitGuaranteeErrors errors={customerDetails?.requestForTransitGuarantee?.error} />
        <TransitGuaranteeCustomsQueryResponseDisplay customsQueryResponse={customerDetails?.transitGuarantees?.[selectedGuaranteeIdx]?.customsQueryResponse} />
        <StickyContainer>
          <Button
            disabled={isButtonDisabled || !form.isValid()}
            onClick={() => handleSubmitCustomer(EditCustomerType.GUARANTEES_TRANSIT, form)}
            type="submit"
            variant="filled"
            size="lg"
          >
            {isCreate ? 'Create' : 'Save'}
          </Button>
        </StickyContainer>
      </ScrollArea>
    </>
  );
};
